export type AudioRouting = 'global' | 'spatial';

export interface OneShotAudioDefinition {
  readonly samples: readonly string[];
  readonly routing: AudioRouting;
  readonly gain: number;
  readonly gainByIntensity: number;
  readonly playbackRate: number;
  readonly playbackRateByIntensity: number;
}

export interface LoopAudioDefinition {
  readonly url: string;
  readonly routing: AudioRouting;
  readonly gain: number;
  readonly fadeInSeconds: number;
  readonly fadeOutSeconds: number;
  readonly loopStartSeconds?: number;
  readonly loopEndSeconds?: number;
}

export interface EngineAudioDefinition {
  readonly loop: LoopAudioDefinition | null;
  readonly idleGain: number;
  readonly speedGain: number;
  readonly minPlaybackRate: number;
  readonly maxPlaybackRate: number;
}

export interface BoostAudioDefinition {
  readonly start: OneShotAudioDefinition | null;
  readonly loop: LoopAudioDefinition | null;
  readonly release: OneShotAudioDefinition | null;
}

export interface SupersonicAudioDefinition {
  readonly enter: OneShotAudioDefinition | null;
  readonly loop: LoopAudioDefinition | null;
}

export interface GameSfxDefinition {
  readonly engine: EngineAudioDefinition;
  readonly boost: BoostAudioDefinition;
  readonly boostPickupSmall: OneShotAudioDefinition | null;
  readonly boostPickupLarge: OneShotAudioDefinition | null;
  readonly jump: OneShotAudioDefinition | null;
  readonly dodge: OneShotAudioDefinition | null;
  readonly doubleJump: OneShotAudioDefinition | null;
  readonly wheelImpact: OneShotAudioDefinition | null;
  readonly ballImpact: OneShotAudioDefinition | null;
  readonly carImpact: OneShotAudioDefinition | null;
  readonly demolition: OneShotAudioDefinition | null;
  readonly flipReset: OneShotAudioDefinition | null;
  readonly supersonic: SupersonicAudioDefinition;
}

export function validateGameSfxDefinition(definition: GameSfxDefinition): void {
  validateEngine(definition.engine);
  validateOptionalOneShot(definition.boost.start, 'boost start');
  validateOptionalLoop(definition.boost.loop, 'boost loop');
  validateOptionalOneShot(definition.boost.release, 'boost release');
  validateOptionalOneShot(definition.boostPickupSmall, 'small boost pickup');
  validateOptionalOneShot(definition.boostPickupLarge, 'large boost pickup');
  validateOptionalOneShot(definition.jump, 'jump');
  validateOptionalOneShot(definition.dodge, 'dodge');
  validateOptionalOneShot(definition.doubleJump, 'double jump');
  validateOptionalOneShot(definition.wheelImpact, 'wheel impact');
  validateOptionalOneShot(definition.ballImpact, 'ball impact');
  validateOptionalOneShot(definition.carImpact, 'car impact');
  validateOptionalOneShot(definition.demolition, 'demolition');
  validateOptionalOneShot(definition.flipReset, 'flip reset');
  validateOptionalOneShot(definition.supersonic.enter, 'supersonic enter');
  validateOptionalLoop(definition.supersonic.loop, 'supersonic loop');
}

export function validateOneShotAudioDefinition(definition: OneShotAudioDefinition, label: string): void {
  if (!Array.isArray(definition.samples) || definition.samples.length === 0 || definition.samples.length > 32) {
    throw new Error(`${label} must contain between 1 and 32 samples.`);
  }
  for (const sample of definition.samples) {
    if (typeof sample !== 'string' || !sample.trim()) throw new Error(`${label} contains an empty sample URL.`);
  }
  validateRouting(definition.routing, label);
  nonNegative(definition.gain, `${label} gain`);
  nonNegative(definition.gainByIntensity, `${label} intensity gain`);
  positive(definition.playbackRate, `${label} playback rate`);
  finite(definition.playbackRateByIntensity, `${label} intensity playback rate`);
  if (definition.playbackRate + Math.min(0, definition.playbackRateByIntensity) <= 0) {
    throw new Error(`${label} intensity playback rate can make playback non-positive.`);
  }
}

export function validateLoopAudioDefinition(definition: LoopAudioDefinition, label: string): void {
  if (!definition.url.trim()) throw new Error(`${label} URL must not be empty.`);
  validateRouting(definition.routing, label);
  nonNegative(definition.gain, `${label} gain`);
  nonNegative(definition.fadeInSeconds, `${label} fade-in`);
  nonNegative(definition.fadeOutSeconds, `${label} fade-out`);
  if (definition.loopStartSeconds !== undefined) nonNegative(definition.loopStartSeconds, `${label} loop start`);
  if (definition.loopEndSeconds !== undefined) positive(definition.loopEndSeconds, `${label} loop end`);
  if (definition.loopStartSeconds !== undefined && definition.loopEndSeconds !== undefined
    && definition.loopEndSeconds <= definition.loopStartSeconds) {
    throw new Error(`${label} loop end must be after loop start.`);
  }
}

function validateEngine(definition: EngineAudioDefinition): void {
  validateOptionalLoop(definition.loop, 'engine loop');
  nonNegative(definition.idleGain, 'engine idle gain');
  nonNegative(definition.speedGain, 'engine speed gain');
  positive(definition.minPlaybackRate, 'engine minimum playback rate');
  positive(definition.maxPlaybackRate, 'engine maximum playback rate');
  if (definition.maxPlaybackRate < definition.minPlaybackRate) {
    throw new Error('Engine maximum playback rate must be >= minimum playback rate.');
  }
}

function validateOptionalOneShot(definition: OneShotAudioDefinition | null, label: string): void {
  if (definition) validateOneShotAudioDefinition(definition, label);
}

function validateOptionalLoop(definition: LoopAudioDefinition | null, label: string): void {
  if (definition) validateLoopAudioDefinition(definition, label);
}

function validateRouting(value: AudioRouting, label: string): void {
  if (value !== 'global' && value !== 'spatial') throw new Error(`${label} routing must be global or spatial.`);
}

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`${label} must be finite.`);
}

function positive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${label} must be a positive finite number.`);
}

function nonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) throw new Error(`${label} must be a finite non-negative number.`);
}
